import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { getSupabaseClient } from '@kordbooks/supabase-client';
import { useAuth } from '@/providers/auth-provider';
import { useSupabaseAuth } from '@kordbooks/shared-auth';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Eye, EyeOff, ShieldTick, Scan } from '@untitledui/icons';
import { validatePassword } from '@kordbooks/shared-auth';

export default function AccountSettings() {
    const { user, signOut } = useAuth();
    // We use the shared hook for MFA actions
    const { enrollMFA, listFactors, challengeAndVerifyMFA, unenrollMFA } = useSupabaseAuth();
    const navigate = useNavigate();

    // General State
    const [isLoading, setIsLoading] = useState(false);
    const [isSigningOut, setIsSigningOut] = useState(false);

    // Password Form State
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // MFA State
    const [mfaFactors, setMfaFactors] = useState<any[]>([]);
    const [isEnrolling, setIsEnrolling] = useState(false);
    const [qrCode, setQrCode] = useState<string | null>(null);
    const [factorId, setFactorId] = useState<string | null>(null);
    const [verifyCode, setVerifyCode] = useState('');
    const [mfaLoading, setMfaLoading] = useState(false);
    const [mfaMessage, setMfaMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Load MFA factors on mount
    useEffect(() => {
        loadFactors();
    }, []);

    const loadFactors = async () => {
        try {
            const factors = await listFactors();
            setMfaFactors(factors || []);
        } catch (err) {
            console.error("Failed to load MFA factors", err);
        }
    };

    // --- MFA HANDLERS ---

    const handleStartEnrollment = async () => {
        try {
            setMfaLoading(true);
            setMfaMessage(null);
            const response = await enrollMFA();
            setFactorId(response.id);
            setQrCode(response.totp.qr_code);
            setIsEnrolling(true);
        } catch (err: any) {
            setMfaMessage({ type: 'error', text: err.message || 'Failed to start enrollment' });
        } finally {
            setMfaLoading(false);
        }
    };

    const handleVerifyAndEnable = async () => {
        if (!factorId || !verifyCode) return;
        try {
            setMfaLoading(true);
            setMfaMessage(null);
            await challengeAndVerifyMFA(factorId, verifyCode);

            setMfaMessage({ type: 'success', text: 'MFA enabled successfully' });
            setIsEnrolling(false);
            setQrCode(null);
            setVerifyCode('');
            setFactorId(null);
            await loadFactors();
        } catch (err: any) {
            setMfaMessage({ type: 'error', text: 'Invalid code. Please try again.' });
        } finally {
            setMfaLoading(false);
        }
    };

    const handleDisableMFA = async (id: string) => {
        if (!confirm("Are you sure you want to disable MFA? This will make your account less secure.")) return;
        try {
            setMfaLoading(true);
            setMfaMessage(null);
            await unenrollMFA(id);
            await loadFactors();
            setMfaMessage({ type: 'success', text: 'MFA disabled successfully' });
        } catch (err: any) {
            setMfaMessage({ type: 'error', text: err.message || 'Failed to disable MFA' });
        } finally {
            setMfaLoading(false);
        }
    };

    // --- EXISTING HANDLERS ---

    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage(null);

        // Validation
        if (!newPassword) {
            setMessage({ type: 'error', text: 'New password is required' });
            return;
        }
        if (!validatePassword(newPassword)) {
            setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
            return;
        }
        if (newPassword !== confirmPassword) {
            setMessage({ type: 'error', text: 'Passwords do not match' });
            return;
        }

        try {
            setIsLoading(true);
            const supabase = getSupabaseClient();

            const { error } = await supabase.auth.updateUser({
                password: newPassword
            });

            if (error) throw error;

            setMessage({ type: 'success', text: 'Password updated successfully' });
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            const error = err as { message?: string };
            setMessage({ type: 'error', text: error.message || 'Failed to update password' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            setIsSigningOut(true);
            await signOut();
            navigate('/sign-in');
        } catch (error) {
            console.error('Error signing out:', error);
        } finally {
            setIsSigningOut(false);
        }
    };

    return (
        <div className="flex flex-col gap-8 max-w-3xl">
            {/* Page Header */}
            <div>
                <h1 className="text-display-sm font-semibold text-primary md:text-display-md">
                    Settings
                </h1>
                <p className="mt-2 text-sm text-tertiary">
                    Manage your account details and security preferences.
                </p>
            </div>


            {/* Quarterly Period Type Section */}
            <section className="space-y-4">
                <div>
                    <h2 className="text-lg font-medium text-primary">Quarterly Period Type</h2>
                    <p className="text-sm text-tertiary">
                        Manage your quarterly period settings.
                    </p>
                </div>
                <div className="p-6 rounded-xl border border-secondary bg-primary flex items-center">
                    <Button
                        onClick={() => {
                            navigate('/portal/settings/quarterly-type');
                        }}
                    >
                        Configure Quarterly Period Type
                    </Button>
                </div>
            </section>

            {/* Profile Section */}
            <section className="space-y-4">
                <div>
                    <h2 className="text-lg font-medium text-primary">Profile</h2>
                    <p className="text-sm text-tertiary">Your personal account information.</p>
                </div>

                <div className="p-6 rounded-xl border border-secondary bg-primary">
                    <div className="max-w-sm space-y-2">
                        <label className="block text-sm font-medium text-primary">
                            Email address
                        </label>
                        <Input
                            isDisabled={true}
                            value={user?.email || ''}
                            className="bg-secondary/50 text-tertiary"
                            aria-label="Email address"
                        />
                        <p className="text-xs text-tertiary">
                            To change your email, please contact support.
                        </p>
                    </div>
                </div>
            </section>

            {/* MFA Section */}
            <section className="space-y-4">
                <div>
                    <h2 className="text-lg font-medium text-primary">Two-Factor Authentication</h2>
                    <p className="text-sm text-tertiary">Add an extra layer of security to your account.</p>
                </div>

                <div className="p-6 rounded-xl border border-secondary bg-primary">
                    {/* MFA Status Message */}
                    {mfaMessage && (
                        <div className={`mb-4 rounded-lg p-3 text-sm ${mfaMessage.type === 'success'
                            ? 'bg-success-primary/10 text-success-primary'
                            : 'bg-error-primary/10 text-error-primary'
                            }`}>
                            {mfaMessage.text}
                        </div>
                    )}

                    {/* List existing factors */}
                    {mfaFactors.length > 0 ? (
                        <div className="space-y-4">
                            {mfaFactors.map((factor) => (
                                <div key={factor.id} className="flex items-center justify-between p-4 border border-secondary rounded-lg bg-secondary/30">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-success-primary/10 rounded-full text-success-primary">
                                            <ShieldTick className="size-5" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-primary">Authenticator App</p>
                                            <p className="text-xs text-tertiary">Verified • {factor.status}</p>
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        onClick={() => handleDisableMFA(factor.id)}
                                        isDisabled={mfaLoading}
                                    >
                                        Disable
                                    </Button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        !isEnrolling && (
                            <div className="flex items-center justify-between">
                                <div className="max-w-md">
                                    <p className="text-sm font-medium text-primary">Secure your account</p>
                                    <p className="text-sm text-tertiary mt-1">
                                        Use an authenticator app to generate a one-time code when you log in.
                                    </p>
                                </div>
                                <Button onClick={handleStartEnrollment} isDisabled={mfaLoading}>
                                    Setup 2FA
                                </Button>
                            </div>
                        )
                    )}

                    {/* Enrollment Flow */}
                    {isEnrolling && qrCode && (
                        <div className="mt-6 space-y-6 border-t border-secondary pt-6">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <h3 className="text-sm font-medium text-primary flex items-center gap-2">
                                        <Scan className="size-4 text-tertiary" />
                                        1. Scan QR Code
                                    </h3>
                                    <p className="text-sm text-tertiary">
                                        Open your authenticator app (like Google or Microsoft Authenticator) and scan this code.
                                    </p>
                                    <div className="flex justify-start">
                                        <div className="p-2 bg-white rounded-lg border border-secondary inline-block">
                                            <img src={qrCode} alt="MFA QR Code" className="w-40 h-40" />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-sm font-medium text-primary flex items-center gap-2">
                                        <ShieldTick className="size-4 text-tertiary" />
                                        2. Enter Verification Code
                                    </h3>
                                    <p className="text-sm text-tertiary">
                                        Enter the 6-digit code from your app to confirm setup.
                                    </p>

                                    <div className="space-y-3 max-w-xs">
                                        <Input
                                            placeholder="000000"
                                            value={verifyCode}
                                            onChange={setVerifyCode}
                                            maxLength={6}
                                            className="font-mono tracking-widest text-center text-lg"
                                            aria-label="Verification code"
                                        />
                                        <div className="flex gap-3">
                                            <Button
                                                className="w-full"
                                                onClick={handleVerifyAndEnable}
                                                isDisabled={mfaLoading || verifyCode.length !== 6}
                                            >
                                                {mfaLoading ? 'Verifying...' : 'Verify & Enable'}
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    setIsEnrolling(false);
                                                    setQrCode(null);
                                                    setFactorId(null);
                                                }}
                                                isDisabled={mfaLoading}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Password Section */}
            <section className="space-y-4">
                <div>
                    <h2 className="text-lg font-medium text-primary">Security</h2>
                    <p className="text-sm text-tertiary">Update your password to keep your account secure.</p>
                </div>

                <div className="p-6 rounded-xl border border-secondary bg-primary">
                    <form onSubmit={handleUpdatePassword} className="max-w-sm space-y-4">
                        {/* Status Message */}
                        {message && (
                            <div className={`rounded-lg p-3 text-sm ${message.type === 'success'
                                ? 'bg-success-primary/10 text-success-primary'
                                : 'bg-error-primary/10 text-error-primary'
                                }`}>
                                {message.text}
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-primary">
                                New Password
                            </label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={setNewPassword}
                                    placeholder="Enter new password"
                                    isDisabled={isLoading}
                                    aria-label="New Password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-quaternary hover:text-fg-quaternary_hover"
                                >
                                    {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-primary">
                                Confirm Password
                            </label>
                            <Input
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={setConfirmPassword}
                                placeholder="Confirm new password"
                                isDisabled={isLoading}
                                aria-label="Confirm Password"
                            />
                        </div>

                        <div className="pt-2">
                            <Button
                                type="submit"
                                size="md"
                                isDisabled={isLoading}
                            >
                                {isLoading ? 'Updating...' : 'Update Password'}
                            </Button>
                        </div>
                    </form>
                </div>
            </section>

            {/* Account Actions Section */}
            <section className="space-y-4 pb-8">
                <div>
                    <h2 className="text-lg font-medium text-primary">Account</h2>
                </div>

                <div className="p-6 rounded-xl border border-secondary bg-primary flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-medium text-primary">Sign out</h3>
                    </div>
                    <Button
                        onClick={handleSignOut}
                        isDisabled={isSigningOut}
                    >
                        {isSigningOut ? 'Signing out...' : 'Sign out'}
                    </Button>
                </div>
            </section>
        </div>
    );
}
